import { useState, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import CheckIcon from '@mui/icons-material/Check';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useTranslation } from 'react-i18next';
import { useGetAuthApi } from '../../hooks/UseApiCalls';
import { DepartmentDtoIn } from '../../models/in/DepartmentDtoIn';
import styles from './Pickers.module.scss';
import InquiryAnchoredPopover from './InquiryAnchoredPopover';

interface User {
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
}

interface AssigneePickerProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onAssign: (email: string | null, departmentId: number | null) => void;
  currentEmail?: string | null;
  currentDepartmentId?: number | null;
  currentAgentName?: string | null;
  currentDepartmentName?: string | null;
}

type AssignTab = 'agents' | 'departments';

export default function AssigneePicker({
  anchorEl, onClose, onAssign, currentEmail, currentDepartmentId,
  currentAgentName, currentDepartmentName,
}: AssigneePickerProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<AssignTab>('agents');

  const open = !!anchorEl;
  const { data: departments } = useGetAuthApi<DepartmentDtoIn[]>(open ? 'management/departments' : '');
  const { data: users } = useGetAuthApi<User[]>(open ? 'users' : '');

  const adminUsers = useMemo(() =>
    users?.filter(u => u.roles?.includes('Admin')) || [], [users]);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return adminUsers;
    const s = search.toLowerCase();
    return adminUsers.filter(u =>
      u.firstName?.toLowerCase().includes(s) ||
      u.lastName?.toLowerCase().includes(s) ||
      (u.email ?? '').toLowerCase().includes(s));
  }, [adminUsers, search]);

  const filteredDepts = useMemo(() => {
    if (!search.trim()) return departments || [];
    const s = search.toLowerCase();
    return (departments || []).filter(d => (d.name ?? '').toLowerCase().includes(s));
  }, [departments, search]);

  return (
    <InquiryAnchoredPopover open={open} anchorEl={anchorEl} onClose={onClose} width={320}>
      {(currentAgentName || currentDepartmentName) && (
        <div className={styles.assignSummary}>
          {currentAgentName && (
            <div className={styles.assignSummaryRow}>
              <PersonIcon sx={{ fontSize: 14 }} />
              <span className={styles.assignSummaryLabel}>{t('inquiries.assign.title')}:</span>
              <span className={styles.assignSummaryValue}>{currentAgentName}</span>
            </div>
          )}
          {currentDepartmentName && (
            <div className={styles.assignSummaryRow}>
              <GroupIcon sx={{ fontSize: 14 }} />
              <span className={styles.assignSummaryLabel}>{t('inquiries.assign.department')}:</span>
              <span className={styles.assignSummaryValue}>{currentDepartmentName}</span>
            </div>
          )}
        </div>
      )}

      <div className={styles.assignTabs}>
        <button
          type="button"
          className={tab === 'agents' ? styles.assignTabActive : styles.assignTab}
          onClick={() => { setTab('agents'); setSearch(''); }}
        >
          <PersonIcon sx={{ fontSize: 14 }} />
          {t('inquiries.assign.title')}
          {currentEmail && <span className={styles.assignTabDot} />}
        </button>
        <button
          type="button"
          className={tab === 'departments' ? styles.assignTabActive : styles.assignTab}
          onClick={() => { setTab('departments'); setSearch(''); }}
        >
          <GroupIcon sx={{ fontSize: 14 }} />
          {t('inquiries.assign.department')}
          {currentDepartmentId && <span className={styles.assignTabDot} />}
        </button>
      </div>

      <div className={styles.pickerSearchWrapper}>
        <span className={styles.pickerSearchIcon}><SearchIcon sx={{ fontSize: 18 }} /></span>
        <input
          className={styles.pickerSearch}
          placeholder={t('inquiries.assign.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>

      {tab === 'agents' && currentEmail && currentAgentName && (
        <div className={styles.assignCurrentBanner}>
          <SwapHorizIcon sx={{ fontSize: 14 }} />
          {t('inquiries.assign.replaceAgentHint', { name: currentAgentName })}
        </div>
      )}
      {tab === 'departments' && currentDepartmentId && currentDepartmentName && (
        <div className={styles.assignCurrentBanner}>
          <SwapHorizIcon sx={{ fontSize: 14 }} />
          {t('inquiries.assign.replaceDeptHint', { name: currentDepartmentName })}
        </div>
      )}

      {/* ph-mask: הרשימה מציגה שמות וכתובות דואר של צוות המשרד - ממוסך
          בהקלטות PostHog (HAT-1081). */}
      <ul className={`${styles.pickerList} ph-mask`}>
        {tab === 'agents' && (
          <>
            {currentEmail && (
              <li>
                <button type="button" className={styles.pickerItemDanger} onClick={() => { onAssign(null, currentDepartmentId ?? null); onClose(); }}>
                  {t('inquiries.assign.removeAgent')}
                </button>
              </li>
            )}
            {filteredUsers.map((user) => {
              const isSelected = user.email === currentEmail;
              return (
                <li key={user.email}>
                  <button
                    type="button"
                    className={isSelected ? styles.pickerItemSelected : styles.pickerItem}
                    onClick={() => { if (!isSelected) { onAssign(user.email, currentDepartmentId ?? null); onClose(); } }}
                  >
                    <span className={styles.avatar}>
                      {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                    </span>
                    <span className={styles.pickerItemContent}>
                      <span className={styles.pickerItemPrimary}>{user.firstName} {user.lastName}</span>
                      <span className={styles.pickerItemSecondary}>{user.email}</span>
                    </span>
                    {isSelected && (
                      <span className={styles.pickerItemTrailing}>
                        <CheckIcon sx={{ fontSize: 16 }} className={styles.checkIcon} />
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </>
        )}

        {tab === 'departments' && (
          <>
            {currentDepartmentId && (
              <li>
                <button type="button" className={styles.pickerItemDanger} onClick={() => { onAssign(currentEmail ?? null, null); onClose(); }}>
                  {t('inquiries.assign.removeDepartment')}
                </button>
              </li>
            )}
            {filteredDepts.map((dept) => {
              const isSelected = dept.id === currentDepartmentId;
              return (
                <li key={dept.id}>
                  <button
                    type="button"
                    className={isSelected ? styles.pickerItemSelected : styles.pickerItem}
                    onClick={() => { if (!isSelected) { onAssign(currentEmail ?? null, dept.id); onClose(); } }}
                  >
                    <span className={styles.pickerItemContent}>
                      <span className={styles.pickerItemPrimary}>{dept.name}</span>
                      <span className={styles.pickerItemSecondary}>
                        {dept.users.length} {t('inquiries.departments.workers')} · {dept.openInquiryCount} {t('inquiries.assign.openInquiries')}
                      </span>
                    </span>
                    {isSelected && (
                      <span className={styles.pickerItemTrailing}>
                        <CheckIcon sx={{ fontSize: 16 }} className={styles.checkIcon} />
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </>
        )}
      </ul>
    </InquiryAnchoredPopover>
  );
}
